import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  IMeasureFilterValue,
  resetMeasureFiltersAction,
  setMeasureFiltersAction,
} from '../../../../../redux/slices/oldBuilder';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { measureFiltersSelector } from '../../../../../redux/selectors/builder';
import useQueryTypeFromLocation from '../../../../../utils/routes/useQueryTypeFromLocation';
import Popover from '../../../../Popover';
import MeasureFilterForm from '../MeasureFilterForm';
import { BuilderRowActions, RowActionsMenu } from '../../../BuilderActions';
import { useActionsVisibilityContext } from '../../../../ActionsVisibilityContext';
import MeasureFilterDisplayValues from './MeasureFilterDisplayValues';
import { useHighlightingContext } from '../../../../HighlightingContext';
import isNil from 'lodash/isNil';
import { assertNotNil } from '../../../../../utils/types';
import usePopoverController from '../../../../Popover/usePopoverController';
import { SingleSelectOption } from '../../../../Dropdown/SingleSelect';
import { SquareRemoveButton } from '../../../../Button';

const Container = styled.div`
  display: flex;
  column-gap: 8px;
`;

const MeasureFilterDisplay = () => {
  const { closePopover, isPopoverOpen, openPopover, popoverAnchor } =
    usePopoverController<HTMLDivElement>();

  const dispatch = useAppDispatch();

  const queryType = useQueryTypeFromLocation();
  assertNotNil(queryType);

  const measureFilters = useAppSelector(measureFiltersSelector);

  const activeMeasureFilters = useMemo(() => {
    if (!measureFilters?.length) {
      return [];
    }

    return measureFilters.filter((measure) => !isNil(measure.filter));
  }, [measureFilters]);

  const { activateHighlighting, deactivateHighlighting } =
    useHighlightingContext();
  const { actionsVisible, hideActions } = useActionsVisibilityContext();

  const handleSubmitMeasureFilter = (newFilters: IMeasureFilterValue[]) => {
    dispatch(
      setMeasureFiltersAction({
        activeMeasureFilters,
        newFilters,
      }),
    );

    closePopover();
  };

  const handleEdit = () => {
    openPopover();

    deactivateHighlighting();
    hideActions();
  };

  const onRemoveButtonClick = () => {
    dispatch(
      resetMeasureFiltersAction({
        activeMeasureFilters,
      }),
    );
  };

  return (
    <>
      <Container>
        <MeasureFilterDisplayValues
          ref={popoverAnchor}
          measureFilterValues={activeMeasureFilters}
        />
        <BuilderRowActions>
          {actionsVisible && (
            <>
              <RowActionsMenu>
                <SingleSelectOption
                  label={'Edit Filter'}
                  onClick={handleEdit}
                  onMouseEnter={activateHighlighting}
                  onMouseLeave={deactivateHighlighting}
                />
              </RowActionsMenu>
              <SquareRemoveButton
                onClick={onRemoveButtonClick}
                content={'Remove Filter'}
                onMouseEnter={activateHighlighting}
                onMouseLeave={deactivateHighlighting}
              />
            </>
          )}
        </BuilderRowActions>
      </Container>
      {isPopoverOpen && (
        <Popover isOpen close={closePopover} anchor={popoverAnchor.current}>
          <MeasureFilterForm
            options={measureFilters}
            defaultValues={activeMeasureFilters}
            onSubmit={handleSubmitMeasureFilter}
          />
        </Popover>
      )}
    </>
  );
};

export default MeasureFilterDisplay;
